import { VisitDto, VisitTypeDto } from '@ecdlink/core';
import { v4 as uuidv4 } from 'uuid';

const LAST_CHILD_VISIT_TYPE = '24_months';

export const getVisitDates = (
  infantId: string,
  birthDate: Date,
  visitTypes: VisitTypeDto[]
): VisitDto[] => {
  const birthDateObj = new Date(birthDate);
  birthDateObj.setHours(10);

  // add one day to the birth date for correct calculations
  const bDay = new Date(birthDateObj);
  bDay.setDate(birthDateObj.getDate() + 1);

  const visitOffsets: Record<string, (date: Date) => Date> = {
    day_3: (date) => date,
    day_7: (date) => addDays(date, 6),
    week_2: (date) => addDays(date, 13),
    week_4: (date) => addDays(date, 27),
    week_7_to_8: (date) => addDays(date, 48),
    '3_months': (date) => addMonths(date, 3, -1),
    '4_months': (date) => addMonths(date, 4, -1),
    '5_months': (date) => addMonths(date, 5, -1),
    '6_months': (date) => addMonths(date, 6, -1),
    '9_months': (date) => addMonths(date, 9, -1),
    '12_months': (date) => addMonths(date, 12, -1),
    '15_months': (date) => addMonths(date, 15, -1),
    '18_months': (date) => addMonths(date, 18, -1),
    '21_months': (date) => addMonths(date, 21, -1),
    '24_months': (date) => addMonths(date, 24, -1),
    '5_years': (date) => addYears(date, 5),
  };

  // Step 1: Calculate planned visit dates
  const plannedVisits = visitTypes.map((visitType) => {
    const calculateDate = visitOffsets[visitType.name];
    if (!calculateDate) {
      throw new Error(`Unknown visit type: ${visitType.name}`);
    }

    return {
      id: uuidv4(),
      dueDate: '',
      actualVisitDate: '',
      attended: false,
      isCancelled: false,
      startedDate: '',
      plannedVisitDate: calculateDate(bDay).toISOString(),
      orderDate: '',
      visitType: visitType,
      insertedDate: new Date().toISOString(),
      clientId: infantId,
      visitAnswers: [],
    };
  });

  // Step 2: Set due/order dates. Each visit is due the day before the next
  // planned visit, except 24 months (the last active child visit) which is
  // due on its own planned date. The last visit in the list always gets dates
  // even when there is no successor (e.g. after 5_years was deactivated).
  for (let i = 0; i < plannedVisits.length; i++) {
    const visit = plannedVisits[i];
    const isLastVisit = i === plannedVisits.length - 1;

    if (visit.visitType.name === LAST_CHILD_VISIT_TYPE) {
      visit.dueDate = visit.plannedVisitDate;
      visit.orderDate = visit.plannedVisitDate;
      continue;
    }

    if (isLastVisit) {
      const lastDueDate = addDays(
        new Date(visit.plannedVisitDate),
        -1
      ).toISOString();
      visit.dueDate = lastDueDate;
      visit.orderDate = lastDueDate;
      continue;
    }

    const nextVisitDate = new Date(plannedVisits[i + 1].plannedVisitDate);
    const dueDate = addDays(nextVisitDate, -1).toISOString();
    visit.dueDate = dueDate;
    visit.orderDate = dueDate;
  }

  return plannedVisits;
};

// Helpers
const addDays = (date: Date, days: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

const addMonths = (date: Date, months: number, adjustDays = 0): Date => {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  result.setDate(result.getDate() + adjustDays);
  return result;
};

const addYears = (date: Date, years: number): Date => {
  const result = new Date(date);
  result.setFullYear(result.getFullYear() + years);
  return result;
};
